import React, { useState } from 'react';
import './MembershipTiersSection.css';

export const plansData = {
  individual: {
    id: 'individual',
    tagline: 'FOR ONE MEMBER',
    title: 'Individual',
    subtitle: 'A private, physician-led year. Assessment, coaching, and a line that is actually answered.',
    features: [
      'Annual executive assessment',
      'Monthly clinical touchpoints',
      '24-hour member line',
      'In-hospital advocacy',
      'Coordination beyond Toronto'
    ],
    prospectusTag: 'A TYPICAL FIRST YEAR · INDIVIDUAL',
    prospectusTitle: 'Individual prospectus',
    prospectusDesc: 'A shape, not a guarantee. Your year is written after the intake, not before it.',
    prospectusSteps: [
      { id: '01', text: 'A long intake — history, not a clipboard' },
      { id: '02', text: 'Multidisciplinary assessment under one roof' },
      { id: '03', text: 'A written strategy you can live with' },
      { id: '04', text: 'Monthly coaching, adjusted as life changes' },
      { id: '05', text: 'The member line, any day' }
    ]
  },
  household: {
    id: 'household',
    tagline: 'FOR A FAMILY',
    title: 'Household',
    subtitle: 'Two adults, dependent children as needed, and one team that knows the whole household.',
    features: [
      'Everything in Individual, for two adults',
      'Dependent children planned individually',
      'A shared family history, separate records',
      'A named case manager',
      'Two household strategy reviews a year'
    ],
    prospectusTag: 'A TYPICAL FIRST YEAR · HOUSEHOLD',
    prospectusTitle: 'Household prospectus',
    prospectusDesc: 'A shape, not a guarantee. Your year is written after the intake, not before it.',
    prospectusSteps: [
      { id: '01', text: 'Separate intakes, so no one is an afterthought' },
      { id: '02', text: 'Assessments scheduled as a household, not a scramble' },
      { id: '03', text: 'Plans that respect different ages and risks' },
      { id: '04', text: 'One manager who knows every chart' },
      { id: '05', text: 'Advocacy and travel support for each member' }
    ]
  },
  corporate: {
    id: 'corporate',
    tagline: 'FOR A LEADERSHIP TEAM',
    title: 'Corporate',
    subtitle: 'Confidential executive care. The company handles logistics. It does not read the chart.',
    features: [
      'Cohort scheduling, private records',
      'A relationship lead for operations only',
      'On-site or in-clinic assessment days',
      'Each executive keeps an individual plan',
      'No employer access without written consent'
    ],
    prospectusTag: 'A TYPICAL FIRST YEAR · CORPORATE',
    prospectusTitle: 'Corporate prospectus',
    prospectusDesc: 'A shape, not a guarantee. Your year is written after the intake, not before it.',
    prospectusSteps: [
      { id: '01', text: 'A logistics conversation — no clinical detail required' },
      { id: '02', text: 'Assessment days arranged around the calendar' },
      { id: '03', text: 'Individual strategies, held by the clinic' },
      { id: '04', text: 'Ongoing member access, not a one-day retreat' },
      { id: '05', text: 'De-identified themes only if members agree' }
    ]
  }
};

export const comparisonMatrix = [
  { feature: 'Annual executive assessment', individual: 'Included', household: 'Two adults', corporate: 'Per executive' },
  { feature: 'Monthly coaching', individual: 'Included', household: 'Included', corporate: 'Included' },
  { feature: '24-hour member line', individual: 'Included', household: 'Included', corporate: 'Included' },
  { feature: 'In-hospital advocacy', individual: 'Included', household: 'Included', corporate: 'Included' },
  { feature: 'Care outside Toronto', individual: 'Included', household: 'Included', corporate: 'Included' },
  { feature: 'Case management', individual: 'Shared team', household: 'Named manager', corporate: 'Relationship lead' },
  { feature: 'Dependent children', individual: '—', household: 'Planned in', corporate: '—' },
  { feature: 'Cohort assessment days', individual: '—', household: '—', corporate: 'Arranged' },
  { feature: 'Employer sees the chart', individual: '—', household: '—', corporate: 'Only with consent' }
];

function MembershipTiersSection({
  consultationLink = "/book-a-consultation/membership"
}) {
  const [activePlanId, setActivePlanId] = useState('household');
  const [showComparison, setShowComparison] = useState(false);

  const activePlan = plansData[activePlanId];

  return (
    <section className="ttc-tiers-section py-5">
      <div className="container py-lg-3">
        
        {/* Header Row */}
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-end justify-content-between mb-5 gap-3">
          <div>
            <div className="ttc-tiers-tagline text-uppercase mb-2 d-flex align-items-center gap-2">
              <span className="ttc-tiers-bullet">•</span>
              <span>MEMBERSHIP</span>
            </div>
            <h2 className="ttc-tiers-heading mb-3">
              Three ways in. <span className="ttc-tiers-italic">One <br /> standard of care.</span>
            </h2>
            <p className="ttc-tiers-subtext mb-0">
              Fees are an annual retainer, discussed privately. We do not publish a price list. <br className="d-none d-md-block" />
              Choose the shape of the relationship — then ask for a conversation.
            </p>
          </div>

          <button
            onClick={() => setShowComparison(!showComparison)}
            className={`ttc-compare-toggle-btn rounded-pill border-0 px-4 py-2 text-decoration-none ${showComparison ? 'active' : ''}`}
            type="button"
          >
            {showComparison ? 'Hide comparison' : 'Compare the three'}
          </button>
        </div>

        {/* 3 Main Cards Grid */}
        <div className="row g-4 mb-4">
          
          {/* Individual Card */}
          <div className="col-12 col-md-4">
            <div
              className={`ttc-plan-card p-4 h-100 ${activePlanId === 'individual' ? 'is-active' : ''}`}
              onClick={() => setActivePlanId('individual')}
              role="button"
              tabIndex={0}
            >
              <span className="ttc-card-tag text-uppercase mb-3 d-block">
                {plansData.individual.tagline}
              </span>
              <h3 className="ttc-card-title mb-3">{plansData.individual.title}</h3>
              <p className="ttc-card-desc mb-4">{plansData.individual.subtitle}</p>
              <ul className="ttc-card-features list-unstyled mb-0">
                {plansData.individual.features.map((feat, i) => (
                  <li key={i} className="d-flex align-items-center gap-2 mb-2">
                    <span className="ttc-check-icon">✓</span>
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Household Card (Dark Featured Styling) */}
          <div className="col-12 col-md-4">
            <div
              className={`ttc-plan-card dark-card p-4 h-100 ${activePlanId === 'household' ? 'is-active' : ''}`}
              onClick={() => setActivePlanId('household')}
              role="button"
              tabIndex={0}
            >
              <span className="ttc-card-tag text-uppercase mb-3 d-block">
                {plansData.household.tagline}
              </span>
              <h3 className="ttc-card-title text-white mb-3">{plansData.household.title}</h3>
              <p className="ttc-card-desc dark-desc mb-4">{plansData.household.subtitle}</p>
              <ul className="ttc-card-features list-unstyled mb-0 text-white">
                {plansData.household.features.map((feat, i) => (
                  <li key={i} className="d-flex align-items-center gap-2 mb-2">
                    <span className="ttc-check-icon gold">✓</span>
                    <span className="fw-medium">{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Corporate Card */}
          <div className="col-12 col-md-4">
            <div
              className={`ttc-plan-card p-4 h-100 ${activePlanId === 'corporate' ? 'is-active' : ''}`}
              onClick={() => setActivePlanId('corporate')}
              role="button"
              tabIndex={0}
            >
              <span className="ttc-card-tag text-uppercase mb-3 d-block">
                {plansData.corporate.tagline}
              </span>
              <h3 className="ttc-card-title mb-3">{plansData.corporate.title}</h3>
              <p className="ttc-card-desc mb-4">{plansData.corporate.subtitle}</p>
              <ul className="ttc-card-features list-unstyled mb-0">
                {plansData.corporate.features.map((feat, i) => (
                  <li key={i} className="d-flex align-items-center gap-2 mb-2">
                    <span className="ttc-check-icon">✓</span>
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

        </div>

        {/* Animated Middle Comparison Table */}
        <div className={`ttc-comparison-wrapper mb-4 ${showComparison ? 'show' : ''}`}>
          <div className="ttc-comparison-inner p-4 p-md-5 rounded-4 bg-white border">
            <div className="table-responsive">
              <table className="table ttc-comparison-table align-middle mb-0">
                <thead>
                  <tr>
                    <th className="ttc-col-feature">Included</th>
                    <th className="ttc-col-plan">Individual</th>
                    <th className="ttc-col-plan">Household</th>
                    <th className="ttc-col-plan">Corporate</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonMatrix.map((row, idx) => (
                    <tr key={idx}>
                      <td className="ttc-feature-name">{row.feature}</td>
                      <td>{row.individual}</td>
                      <td className="fw-semibold">{row.household}</td>
                      <td>{row.corporate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Bottom Prospectus Card (Dynamically updates based on active plan) */}
        <div className="ttc-prospectus-card p-4 p-md-5 rounded-4 bg-white border">
          <div className="row g-4 align-items-center">
            
            {/* Left Column: Details */}
            <div className="col-12 col-lg-5 pe-lg-4">
              <span className="ttc-prospectus-tag text-uppercase mb-2 d-block">
                {activePlan.prospectusTag}
              </span>
              <h3 className="ttc-prospectus-title mb-3">
                {activePlan.prospectusTitle}
              </h3>
              <p className="ttc-prospectus-desc mb-4">
                {activePlan.prospectusDesc}
              </p>
              <a href={consultationLink} className="ttc-request-btn btn btn-primary rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2">
                <span>Request this membership</span>
                <span className="ttc-btn-arrow">→</span>
              </a>
            </div>

            {/* Right Column: Steps */}
            <div className="col-12 col-lg-7">
              <div className="d-flex flex-column gap-3">
                {activePlan.prospectusSteps.map((step) => (
                  <div key={step.id} className="ttc-prospectus-step-item d-flex align-items-center gap-3 pb-3 border-bottom">
                    <span className="ttc-step-num">{step.id}</span>
                    <span className="ttc-step-text">{step.text}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>
  );
}

export default MembershipTiersSection;